import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, FileText, Activity } from "lucide-react";

function humanize(s) {
  return String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function useRelativeTime(timestamp) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!timestamp) return undefined;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [timestamp]);
  if (!timestamp) return null;
  const diff = Math.max(0, Math.round((now - timestamp) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function NextActionHint({ firstMissing, stageLabel, missingDocs }) {
  if (firstMissing) {
    return (
      <span style={{ color: "var(--text-primary)" }}>
        Fill <b>{humanize(firstMissing.field)}</b> in the <b>{stageLabel}</b> stage.
      </span>
    );
  }
  if (missingDocs.length > 0) {
    return (
      <span style={{ color: "var(--text-primary)" }}>
        Upload: <b>{missingDocs[0].label}</b>
      </span>
    );
  }
  return (
    <span style={{ color: "var(--success-text)" }}>
      All required items are collected — review your case.
    </span>
  );
}

function MissingDocsList({ missingDocs }) {
  if (missingDocs.length === 0) return null;
  return (
    <div
      className="mt-3 p-3 rounded-lg text-xs"
      data-testid="missing-documents"
      style={{ background: "#fff", border: "1px solid var(--ai-border)" }}
    >
      <div className="font-semibold mb-1.5" style={{ color: "var(--ai-text)" }}>
        Missing documents
      </div>
      <ul className="space-y-1">
        {missingDocs.map((d) => (
          <li
            key={d.id}
            className="flex items-start gap-1.5"
            style={{ color: "var(--text-primary)" }}
          >
            <FileText className="w-3 h-3 mt-0.5 shrink-0" /> {d.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChatThread({ chatMsgs, chatBusy, scrollRef }) {
  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
      {chatMsgs.length === 0 && !chatBusy && (
        <div
          className="text-xs text-center py-8"
          style={{ color: "var(--text-secondary)" }}
        >
          Say hello in any language to start the walkthrough.
        </div>
      )}
      {chatMsgs.map((m) => {
        const key = m._id || `${m.role}-${m.ts}`;
        return (
          <div key={key} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`ai-msg max-w-[85%] p-3 rounded-xl ${m.role === "user" ? "text-white" : ""}`}
              data-testid={`chat-msg-${m.role}`}
              dir="auto"
              style={{
                background: m.role === "user" ? "var(--action)" : "#fff",
                color: m.role === "user" ? "#fff" : "var(--text-primary)",
                border: m.role === "user" ? "none" : "1px solid var(--ai-border)",
              }}
            >
              {m.content}
            </div>
          </div>
        );
      })}
      {chatBusy && (
        <div className="flex justify-start">
          <div className="p-3 rounded-xl bg-white border" style={{ borderColor: "var(--ai-border)" }}>
            <div className="dot-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIPanel({
  caseData,
  chatMsgs,
  chatBusy,
  chatInput,
  onChangeInput,
  onSend,
  lastSavedAt,
  fieldsSinceChat,
  variant = "desktop",
}) {
  const scrollRef = useRef(null);
  const isSheet = variant === "sheet";

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [chatMsgs, chatBusy]);

  const missingFields = caseData.missing_fields || [];
  const missingDocs = caseData.missing_documents || [];
  const firstMissing = missingFields[0];
  const stageLabel = firstMissing && caseData.stages.find((s) => s.id === firstMissing.stage)?.label;
  const savedRel = useRelativeTime(lastSavedAt);
  const showActivity = savedRel || fieldsSinceChat > 0;

  return (
    <aside
      className={
        isSheet
          ? "flex flex-col h-full w-full"
          : "w-96 shrink-0 border-l flex flex-col"
      }
      style={{
        borderColor: "var(--border-stone)",
        background: "var(--ai-bg)",
      }}
      data-testid="ai-panel"
    >
      <div className="p-5 border-b" style={{ borderColor: "var(--ai-border)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
          <div className="font-display font-bold text-sm" style={{ color: "var(--ai-text)" }}>
            Case Companion
          </div>
          <span className="pill pill-ok text-[10px]">Online</span>
        </div>
        <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
          Ask in any language — English, Español, हिन्दी, 中文, العربية, বাংলা, and more.
        </div>

        {showActivity && (
          <div
            className="mb-3 flex items-center gap-1.5 text-[11px]"
            data-testid="session-activity-indicator"
            style={{ color: "var(--ai-text)" }}
            aria-live="polite"
          >
            <Activity className="w-3 h-3" />
            <span>
              {savedRel && <>Auto-saved {savedRel}</>}
              {savedRel && fieldsSinceChat > 0 && " · "}
              {fieldsSinceChat > 0 && (
                <>
                  {fieldsSinceChat} field{fieldsSinceChat === 1 ? "" : "s"} since last chat
                </>
              )}
            </span>
          </div>
        )}

        <div
          className="p-3 rounded-lg text-xs"
          data-testid="next-recommended-action"
          style={{ background: "#fff", border: "1px solid var(--ai-border)" }}
        >
          <div className="font-semibold mb-1" style={{ color: "var(--ai-text)" }}>
            Next recommended action
          </div>
          <NextActionHint firstMissing={firstMissing} stageLabel={stageLabel} missingDocs={missingDocs} />
        </div>

        <MissingDocsList missingDocs={missingDocs} />
      </div>

      <ChatThread chatMsgs={chatMsgs} chatBusy={chatBusy} scrollRef={scrollRef} />

      <div className="p-4 border-t" style={{ borderColor: "var(--ai-border)" }}>
        <div className="flex items-end gap-2">
          <textarea
            data-testid="ai-chat-input"
            dir="auto"
            rows={2}
            className="flex-1 resize-none field-input text-sm"
            placeholder="Ask anything about your case…"
            value={chatInput}
            onChange={(e) => onChangeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          <button
            data-testid="ai-chat-send-btn"
            onClick={onSend}
            disabled={chatBusy || !chatInput.trim()}
            className="btn-primary p-3"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
